import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import CustomFieldEnumMultipleEdit from '@atom/components/common/customFieldEnumEdit/customFieldEnumMultipleEdit';
import CustomFieldEnumNestedEdit from '@atom/components/common/customFieldEnumEdit/CustomFieldEnumNestedEdit';
import CustomFieldEnumRootEdit from '@atom/components/common/customFieldEnumEdit/CustomFieldEnumRootEdit';
import FieldViewAsToggle from '@atom/components/common/fieldViewAsToggle/FieldViewAsToggle';
import { Icon, IconButton, Modal, Select, TextField } from '@atom/mui';
import { ArrowBackIcon } from '@atom/mui/icons';
import { customFieldUnitsSelector } from '@atom/selectors/preferencesSelectors';
import {
  DataType,
  FieldViewType,
  WorkOrderFieldDataType,
} from '@atom/types/dataType';
import {
  SubField,
  TEMPLATE_VALUE_WORK_TYPES,
  ValueDictionary,
  WORK_ORDER_FIELD_DATA_TYPE_OPTIONS,
  WorkOrderDropdownField,
  WorkOrderField,
  WorkOrderFieldCreatePayload,
} from '@atom/types/work';
import {
  isNilOrEmpty,
  isValidUrlWithMessage,
  MAX_HYPERLINK_CHARS,
  VALIDATE_HYPERLINK_DEBOUNCE_MS,
} from '@atom/utilities/validationUtilities';
import {
  getActiveSubFieldPathLabel,
  getNestedDepth,
  getPath,
  hydrateDropdownField,
  isOverListOptionLimit,
  mapDropdownField,
  MAX_LIST_OPTIONS,
  MIN_SINGLE_SELECT_OPTIONS,
  removeSubFieldByMatchValue,
  updateSubFields,
  VIEW_AS_DATA_TYPES,
} from '@atom/utilities/workOrderFieldUtilities';

import WorkOrderModalRequiredFields from './WorkOrderModalRequiredFields';

import classes from './workOrderFields.module.css';

const { MenuItem } = Select;

const enum ModalView {
  ROOT = 'ROOT',
  NESTED = 'NESTED',
}

const styles = {
  inputContainer: {
    marginTop: '1rem',
    marginBottom: '2rem',
  },
  inputContainerInline: {
    display: 'inline-block',
    width: '48%',
    marginTop: '1rem',
    marginRight: '1rem',
  },
  fieldTypeSelect: {
    width: '65%',
  },
  unitSelect: {
    display: 'inline-block',
    width: '48%',
    marginTop: '1rem',
    textTransform: 'uppercase',
  },
  contentStyle: {
    padding: '3rem',
    overflow: 'auto',
  },
  upper: {
    textTransform: 'uppercase',
  },
};

interface Props {
  fields: WorkOrderField[];
  handleCreate: (payload: WorkOrderFieldCreatePayload) => void;
  loadingCreate: boolean;
  onClose: () => void;
  open: boolean;
  isWorkTemplate?: boolean;
  errorText?: string;
}

const WorkOrderFieldCreateModal = ({
  fields,
  handleCreate,
  loadingCreate,
  onClose,
  open,
  isWorkTemplate = false,
  errorText = '',
}: Props) => {
  const unitTypes = useSelector(customFieldUnitsSelector);

  const [view, setView] = useState<ModalView>(ModalView.ROOT);
  const [title, setTitle] = useState<string>('');
  const [hyperlink, setHyperlink] = useState<string>('');
  const [urlErrorMsg, setUrlErrorMsg] = useState<string>('');
  const [dataType, setDataType] = useState<WorkOrderFieldDataType>(
    DataType.SUMMARY,
  );
  const [value, setValue] = useState<string>('');
  const [dropdownField, setDropdownField] = useState<WorkOrderDropdownField>();
  const [activeKey, setActiveKey] = useState<string>();
  const [activePath, setActivePath] = useState<any[]>([]);

  const [valueDictionary, setValueDictionary] = useState<ValueDictionary>();
  const [enumeration, setEnumeration] = useState<string[]>([]);
  const [subFields, setSubFields] = useState<SubField[]>([]);
  const [units, setUnits] = useState<string>('');
  const [viewAs, setViewAs] = useState<FieldViewType | null>(null);

  // Required on Create only applies when creating new WO
  // - required is that standard "Required on complete"
  const [requiredOnCreate, setRequiredOnCreate] = useState<boolean>(false);
  const [required, setRequired] = useState<boolean>(false);

  // For Multiselect fields only
  // - Default required field require only one selection
  const [selectAllOnCreateWork, setSelectAllOnCreateWork] =
    useState<boolean>(false);
  const [selectAllOnCompleteWork, setSelectAllOnCompleteWork] =
    useState<boolean>(false);

  // Applies value dictionary to mapped field tree to produce
  // the properly hydrated field tree.
  useEffect(() => {
    const hydratedField = hydrateDropdownField(
      { enumeration, subFields },
      valueDictionary,
      required,
      requiredOnCreate,
    );

    setDropdownField(hydratedField);
  }, [enumeration, subFields, valueDictionary, required, requiredOnCreate]);

  useEffect(() => {
    const newActivePath = getPath(activeKey, subFields);
    const newView =
      R.length(newActivePath) === 0 ? ModalView.ROOT : ModalView.NESTED;

    setActivePath(newActivePath);
    setView(newView);
  }, [activeKey]);

  // Set Multiselect Select All options to false
  // if parent required option is ever false
  useEffect(() => {
    if (!requiredOnCreate) {
      setSelectAllOnCreateWork(false);
    }
  }, [requiredOnCreate]);
  useEffect(() => {
    if (!required) {
      setSelectAllOnCompleteWork(false);
    }
  }, [required]);

  const resetState = () => {
    setDataType(DataType.SUMMARY);
    setView(ModalView.ROOT);
    setTitle('');
    setValue('');
    setHyperlink('');
    setDropdownField(null);
    setValueDictionary(null);
    setEnumeration([]);
    setSubFields([]);
    setActivePath([]);
    setActiveKey(null);
    setUnits('');
    setViewAs(null);
    setRequiredOnCreate(false);
    setRequired(false);
    setSelectAllOnCompleteWork(false);
    setSelectAllOnCreateWork(false);
  };

  const onDataTypeChange = (type: WorkOrderFieldDataType) => {
    setTitle('');
    setValue('');
    setViewAs(null);
    setDataType(type);

    const initialDropdownField: WorkOrderDropdownField = {
      enumeration: type === DataType.ENUM_SINGLE ? [''] : ['', ''],
      subFields: [],
    };

    if (type === DataType.ENUM_SINGLE || type === DataType.ENUM_MULTIPLE) {
      // Transforms field tree into mapped field tree with value
      // dictionary for ease of create and edit.
      const mappedField = mapDropdownField(initialDropdownField);

      setDropdownField(initialDropdownField);
      setValueDictionary(mappedField.valueDictionary);
      setEnumeration(mappedField.enumeration);
      setSubFields(mappedField.subFields);
    } else {
      setDropdownField(null);
    }
  };

  const showViewAsToggle = VIEW_AS_DATA_TYPES.includes(dataType);

  // Options linked to child dropdowns can't be displayed as a list
  const hasCascadingDropdowns = !isNilOrEmpty(subFields);

  // The option limit only applies while the field is displayed as a list
  const isListView =
    showViewAsToggle &&
    !hasCascadingDropdowns &&
    viewAs === FieldViewType.CHECKBOX;
  const isOverListLimit =
    isListView && isOverListOptionLimit(enumeration.length);

  const handleSubmit = () => {
    handleCreate({
      dataType,
      title,
      ...R.reject(isNilOrEmpty, {
        value,
        units,
        hyperlink,
        enumeration: dropdownField?.enumeration,
        subFields: dropdownField?.subFields,
        required,
        requiredOnCreate,
        selectAllOnCompleteWork,
        selectAllOnCreateWork,
        // Dropdown is the default, so a null viewAs is omitted on create
        viewAs: showViewAsToggle && !hasCascadingDropdowns ? viewAs : null,
      }),
    });
  };

  const debouncedSetHyperlink = useMemo(
    () =>
      debounce(event => {
        setHyperlink(event.target.value);
      }, VALIDATE_HYPERLINK_DEBOUNCE_MS),
    [],
  );

  const isNameInUse = useMemo(() => {
    return fields.some(
      field => field.title.toLowerCase() === title.toLowerCase(),
    )
      ? 'This name is already in use.'
      : '';
  }, [title, fields]);

  const isValid = useMemo(() => {
    const validEnums = dropdownField?.enumeration.reduce(
      (acc, option) => (!R.isEmpty(option.trim()) ? acc + 1 : acc),
      0,
    );

    const isNameValid = isWorkTemplate || !isNameInUse;
    const [isURLValid, urlError] = isValidUrlWithMessage(hyperlink);
    setUrlErrorMsg(urlError);
    const isHyperlinkValid =
      dataType === DataType.HYPERLINK
        ? !R.isEmpty(hyperlink) &&
          isURLValid &&
          !R.isEmpty(value) &&
          value?.length <= MAX_HYPERLINK_CHARS
        : true;

    return (
      isNameValid &&
      isHyperlinkValid &&
      !R.isEmpty(title) &&
      (dataType !== DataType.ENUM_SINGLE ||
        validEnums >= MIN_SINGLE_SELECT_OPTIONS) &&
      (dataType !== DataType.ENUM_MULTIPLE || validEnums >= 2)
    );
  }, [title, value, hyperlink, isNameInUse, dropdownField, isWorkTemplate]);

  const navigateBack = async () => {
    // drops the last 3 entries in the activePath in order
    // to access the parent nested field;
    const parentPath = R.dropLast(3, activePath);
    const parent: SubField = R.view(R.lensPath(parentPath), subFields);
    await setActiveKey(parent.matchValue);
  };

  const updateSubField = (subField: SubField) => {
    setSubFields(updateSubFields(subField, subFields));
  };

  const removeSubField = async (key: string) => {
    await navigateBack();
    setSubFields(removeSubFieldByMatchValue(subFields, key));
  };

  const activeSubField = R.view(R.lensPath(activePath), subFields);
  const nestedDepth = getNestedDepth(activeKey, subFields);
  const pathLabel = getActiveSubFieldPathLabel(
    title,
    activePath,
    subFields,
    valueDictionary,
  );

  const modalTitle =
    view === ModalView.ROOT ? (
      'Add Field'
    ) : (
      <div className={classes.modalTitle}>
        <IconButton
          onClick={navigateBack}
          size="small"
          data-cy="workOrderFieldCreateModalBackButton"
        >
          <Icon icon={ArrowBackIcon} />
        </IconButton>
        <div className={classes.modalTitlePath}>{pathLabel}</div>
      </div>
    );

  const error = isWorkTemplate ? errorText : isNameInUse;

  const content = {
    [ModalView.ROOT]: (
      <>
        <Select
          label="Field Type"
          value={dataType}
          style={styles.fieldTypeSelect}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onDataTypeChange(event.target.value as WorkOrderFieldDataType)
          }
          data-cy="workOrderFieldCreateModalFieldTypeSelect"
        >
          {WORK_ORDER_FIELD_DATA_TYPE_OPTIONS.map(option => (
            <MenuItem key={option.dataType} value={option.dataType}>
              <div className={classes.menuItem}>
                <Icon style={{ marginRight: '0.5rem' }} icon={option.icon} />
                {option.text}
              </div>
            </MenuItem>
          ))}
        </Select>
        {showViewAsToggle && (
          <FieldViewAsToggle
            viewAs={viewAs}
            onChange={setViewAs}
            listDisabled={hasCascadingDropdowns}
            optionCount={enumeration.length}
            dataCy="workOrderFieldCreateModalViewAsToggle"
          />
        )}
        <TextField
          id="title"
          label="Title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          style={
            dataType === DataType.NUMBER
              ? styles.inputContainerInline
              : styles.inputContainer
          }
          // ensure modal is open to prevent "flash" of error during close animation
          error={open && !!error}
          helperText={open ? error : ''}
          required
          data-cy="workOrderFieldCreateModalTitleInput"
        />
        {dataType === DataType.NUMBER && (
          <Select
            label="Unit"
            value={units || ''}
            style={styles.unitSelect}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUnits(event.target.value)
            }
            data-cy="workOrderFieldCreateModalUnitSelect"
          >
            <MenuItem value="" />
            {unitTypes.map(type => (
              <MenuItem key={type.unit} value={type.unit} style={styles.upper}>
                {type.unit}
              </MenuItem>
            ))}
          </Select>
        )}
        {dataType === DataType.SUMMARY && (
          <TextField
            id="description"
            label="Description Text"
            value={value}
            onChange={event => setValue(event.target.value)}
            style={styles.inputContainer}
            multiline
            data-cy="workOrderFieldCreateModalDescriptionInput"
          />
        )}
        {dataType === DataType.ENUM_SINGLE && (
          <CustomFieldEnumRootEdit
            maxOptions={isListView ? MAX_LIST_OPTIONS : undefined}
            hideLinkOptions={isListView}
            valueDictionary={valueDictionary}
            setValueDictionary={setValueDictionary}
            enumeration={enumeration}
            setEnumeration={setEnumeration}
            subFields={subFields}
            setSubFields={setSubFields}
            setActiveKey={setActiveKey}
          />
        )}
        {dataType === DataType.ENUM_MULTIPLE && (
          <CustomFieldEnumMultipleEdit
            maxOptions={isListView ? MAX_LIST_OPTIONS : undefined}
            valueDictionary={valueDictionary}
            setValueDictionary={setValueDictionary}
            enumeration={enumeration}
            setEnumeration={setEnumeration}
          />
        )}
        {dataType === DataType.HYPERLINK && (
          <>
            <TextField
              id="hyperlink"
              label="URL"
              defaultValue={hyperlink}
              onChange={debouncedSetHyperlink}
              style={styles.inputContainer}
              error={urlErrorMsg.length > 0}
              helperText={urlErrorMsg}
              required
              data-cy="workOrderFieldCreateModalUrlInput"
            />
            <TextField
              id="text_to_display"
              label="Text to Display"
              value={value}
              onChange={event => setValue(event.target.value)}
              style={styles.inputContainer}
              helperText={`${value?.length}/${MAX_HYPERLINK_CHARS}`}
              error={value?.length > MAX_HYPERLINK_CHARS}
              slotProps={{ formHelperText: { style: { textAlign: 'right' } } }}
              required
              data-cy="workOrderFieldCreateModalDisplayTextInput"
            />
          </>
        )}
        {isWorkTemplate && !TEMPLATE_VALUE_WORK_TYPES.includes(dataType) && (
          <WorkOrderModalRequiredFields
            dataType={dataType}
            hasSubFields={!isNilOrEmpty(subFields)}
            requiredOnCreate={requiredOnCreate}
            required={required}
            selectAllOnCreateWork={selectAllOnCreateWork}
            selectAllOnCompleteWork={selectAllOnCompleteWork}
            setSelectAllOnCreateWork={setSelectAllOnCreateWork}
            setSelectAllOnCompleteWork={setSelectAllOnCompleteWork}
            setRequiredOnCreate={setRequiredOnCreate}
            setRequired={setRequired}
          />
        )}
      </>
    ),
    [ModalView.NESTED]: (
      <CustomFieldEnumNestedEdit
        // @ts-ignore
        subField={activeSubField}
        updateSubField={updateSubField}
        valueDictionary={valueDictionary}
        setValueDictionary={setValueDictionary}
        removeSubField={removeSubField}
        setActiveKey={setActiveKey}
        nestedDepth={nestedDepth}
      />
    ),
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onExited={resetState}
      onCancel={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Add"
      loading={loadingCreate}
      disabled={!isValid || isOverListLimit}
      contentStyle={styles.contentStyle}
      disableFooter={view === ModalView.NESTED}
      data-cy="workOrderFieldCreateModal"
    >
      {content[view]}
    </Modal>
  );
};

export default WorkOrderFieldCreateModal;
